// buddy-auswahl.js — markierten Text an den Buddy geben.
//
// Wer im Befund oder in einer Vault-Notiz etwas markiert, bekommt an der
// Markierung einen Knopf „Buddy fragen"; ein Klick öffnet dort ein Feld für
// die Frage, und die Antwort erscheint als schwebende Kachel an derselben
// Stelle — der Chat links bleibt zu. Gefragt wird über denselben Endpunkt
// (`POST /api/buddy/chat`), nur ohne Verlauf: jede Markierung ist eine
// eigene, kurze Frage.

import { anfrageBody, ndjsonPuffer, quellenMarkdown, hinweiseMarkdown } from './buddy-chat.js';
import { renderMarkdown } from './markdown-renderer.js';
import { processObsidianCitations, quellenZeileMarkieren } from './obsidian-zitate.js';

const MAX_AUSWAHL = 1500;

/// Die Nachricht, die im Chat landet.
///
/// Die Markierung steht in Anführungszeichen darüber, damit der Buddy sie vom
/// Text der Frage unterscheiden kann; die Quelle sagt, ob sie aus dem Befund
/// oder aus einer Notiz stammt (dann mit deren Titel).
export function frageMitAuswahl(auswahl, quelle, frage) {
  const markiert = String(auswahl || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_AUSWAHL);
  const text = String(frage || '').trim();
  if (!markiert) return text;

  const woher = quelle && quelle.titel ? `${quelle.art} „${quelle.titel}"` : quelle?.art || 'Markierung';
  const kopf = `Zu dieser Stelle aus ${woher}:\n„${markiert}"`;
  return text ? `${kopf}\n\n${text}` : kopf;
}

/// Wohin der Knopf gehört: über die Markierung, damit er nicht unter dem
/// Mauszeiger klebt. Nur wenn oben kein Platz mehr ist, weicht er nach unten.
/// Gibt Koordinaten relativ zum Ansichtsfenster zurück.
export function knopfStelle(rechteck, fenster, knopf = { breite: 150, hoehe: 32 }) {
  const rand = 8;
  const links = Math.min(
    Math.max(rand, rechteck.left),
    Math.max(rand, fenster.breite - knopf.breite - rand)
  );
  const obenPlatz = rechteck.top > knopf.hoehe + rand;
  const oben = obenPlatz
    ? rechteck.top - knopf.hoehe - 6
    : Math.min(rechteck.bottom + 6, fenster.hoehe - knopf.hoehe - rand);
  return { links, oben };
}

function elementGehoertZu(knoten, wurzeln) {
  const element = knoten?.nodeType === 3 ? knoten.parentElement : knoten;
  return wurzeln.some((w) => w && element && w.contains(element));
}

/// Quelle der Markierung: Vault-Notiz (mit Titel) oder Befund im Editor.
function quelleBestimmen(knoten) {
  const element = knoten?.nodeType === 3 ? knoten.parentElement : knoten;
  const inVault = element?.closest?.('.vault-markdown-content');
  if (inVault) {
    const titel =
      document.getElementById('vault-current-file')?.textContent?.trim() ||
      document.querySelector('.vault-current-file')?.textContent?.trim() ||
      '';
    return { art: 'der Notiz', titel };
  }
  return { art: 'dem Befund', titel: '' };
}

export function initBuddyAuswahl() {
  if (document.getElementById('buddy-auswahl')) return;

  const box = document.createElement('div');
  box.id = 'buddy-auswahl';
  box.className = 'buddy-auswahl';
  box.innerHTML = `
    <button type="button" class="buddy-auswahl-knopf"><svg class="buddy-auswahl-icon" viewBox="0 0 320 320" width="17" height="17" fill="none" stroke="currentColor" stroke-width="18" stroke-linejoin="round" aria-hidden="true"><path d="M120 48H200V64H232V80H248V112H264V144H280V208H264V232H248V248H232V264H88V248H72V232H56V208H40V144H56V112H72V80H88V64H120Z"/><rect x="102" y="152" width="16" height="44" fill="currentColor" stroke="none"/><rect x="202" y="152" width="16" height="44" fill="currentColor" stroke="none"/></svg><span>Buddy fragen</span></button>
    <form class="buddy-auswahl-form">
      <input type="text" class="buddy-auswahl-feld" placeholder="Frage zur Markierung…" />
      <button type="submit" class="buddy-auswahl-senden" title="Fragen" aria-label="Fragen">
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M18,12h0a2,2,0,0,0-.59-1.4l-4.29-4.3a1,1,0,0,0-1.41,0,1,1,0,0,0,0,1.42L15,11H5a1,1,0,0,0,0,2H15l-3.29,3.29a1,1,0,0,0,1.41,1.42l4.29-4.3A2,2,0,0,0,18,12Z"/></svg>
      </button>
    </form>
    <div class="buddy-auswahl-antwort">
      <div class="buddy-auswahl-kopf">
        <span class="buddy-auswahl-frage"></span>
        <button type="button" class="buddy-auswahl-zu" title="Schließen" aria-label="Schließen">✕</button>
      </div>
      <div class="buddy-auswahl-text"></div>
    </div>`;
  document.body.appendChild(box);

  const knopf = box.querySelector('.buddy-auswahl-knopf');
  const form = box.querySelector('.buddy-auswahl-form');
  const feld = box.querySelector('.buddy-auswahl-feld');

  let auswahl = '';
  let quelle = { art: 'dem Befund', titel: '' };
  let letztesRechteck = { left: 0, top: 0, bottom: 0 };

  const schliessen = () => {
    abbruch?.abort();
    box.classList.remove('offen', 'fragt', 'antwortet');
    feld.value = '';
  };

  const zeigen = (rechteck, text, woher) => {
    auswahl = text;
    quelle = woher;
    letztesRechteck = rechteck;
    const { links, oben } = knopfStelle(rechteck, {
      breite: window.innerWidth,
      hoehe: window.innerHeight,
    });
    box.style.left = `${links}px`;
    box.style.top = `${oben}px`;
    box.classList.add('offen');
    box.classList.remove('fragt');
  };

  document.addEventListener('mouseup', (ev) => {
    if (box.contains(ev.target)) return;
    // Der Klick beendet die Markierung erst nach diesem Ereignis.
    setTimeout(() => {
      const sel = window.getSelection();
      const text = sel ? sel.toString().trim() : '';
      const wurzeln = [
        document.querySelector('.vault-markdown-content'),
        document.querySelector('#markdown-editor .cm-content'),
      ];
      if (!text || !sel.rangeCount || !elementGehoertZu(sel.anchorNode, wurzeln)) {
        // Eine offene Antwort bleibt stehen, bis man sie schliesst.
        if (!box.classList.contains('antwortet')) schliessen();
        return;
      }
      const rechteck = sel.getRangeAt(0).getBoundingClientRect();
      zeigen(rechteck, text, quelleBestimmen(sel.anchorNode));
    }, 0);
  });

  knopf.addEventListener('click', (ev) => {
    ev.stopPropagation();
    box.classList.add('fragt');
    feld.focus();
  });

  const antwortFeld = box.querySelector('.buddy-auswahl-text');
  const frageZeile = box.querySelector('.buddy-auswahl-frage');
  let abbruch = null;

  box.querySelector('.buddy-auswahl-zu').addEventListener('click', (ev) => {
    ev.stopPropagation();
    schliessen();
  });

  /// Antwort in die Kachel setzen: Markdown rendern, Vault-Belege klickbar
  /// machen (wie im Chat) und Literaturlinks in einem neuen Tab oeffnen.
  const setzen = (markdown) => {
    antwortFeld.innerHTML = renderMarkdown(markdown);
    processObsidianCitations(antwortFeld);
    quellenZeileMarkieren(antwortFeld);
    antwortFeld.querySelectorAll('a[href^="http"]').forEach((a) => {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    });
  };

  /// Die Antwort kommt als NDJSON-Strom: Fortschritt, dann Text mit Quellen.
  const fragen = async (nachricht) => {
    abbruch?.abort();
    abbruch = new AbortController();
    antwortFeld.innerHTML = '<p class="buddy-auswahl-schritt">Frage den Buddy…</p>';
    try {
      const antwort = await fetch('/api/buddy/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(anfrageBody(nachricht, window.editorAPI?.getText?.() ?? '', [])),
        signal: abbruch.signal,
      });
      if (!antwort.ok) throw new Error(`Server antwortete mit ${antwort.status}`);

      const leser = antwort.body.getReader();
      const dekoder = new TextDecoder();
      const puffer = ndjsonPuffer();
      let fertig = false;
      const verarbeite = (ereignis) => {
        if (ereignis.typ === 'schritt') {
          antwortFeld.innerHTML = `<p class="buddy-auswahl-schritt">${ereignis.text}</p>`;
        } else if (ereignis.typ === 'antwort') {
          setzen(
            ereignis.text + hinweiseMarkdown(ereignis.hinweise) + quellenMarkdown(ereignis.belege)
          );
          fertig = true;
        } else if (ereignis.typ === 'fehler') {
          setzen(`**${ereignis.text}**` + quellenMarkdown(ereignis.belege));
          fertig = true;
        }
      };
      for (;;) {
        const { value, done } = await leser.read();
        if (done) break;
        puffer.zeilen(dekoder.decode(value, { stream: true })).forEach(verarbeite);
      }
      puffer.ende().forEach(verarbeite);
      if (!fertig) {
        antwortFeld.textContent = 'Die Verbindung brach ab, bevor eine Antwort kam.';
      }
    } catch (fehler) {
      if (fehler.name === 'AbortError') return;
      antwortFeld.textContent = `Fehler: ${fehler.message}`;
    }
  };

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const frage = feld.value.trim();
    if (!frage) return;
    frageZeile.textContent = frage;
    box.classList.remove('fragt');
    box.classList.add('antwortet');
    // Die Kachel ist hoeher als der Knopf: neu einpassen, damit sie im
    // Fenster bleibt.
    const stelle = knopfStelle(letztesRechteck, {
      breite: window.innerWidth,
      hoehe: window.innerHeight,
    }, { breite: 380, hoehe: 260 });
    box.style.left = `${stelle.links}px`;
    box.style.top = `${stelle.oben}px`;
    fragen(frageMitAuswahl(auswahl, quelle, frage));
  });

  feld.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') {
      ev.stopPropagation();
      schliessen();
    }
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') schliessen();
  });
}
